import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { Box, CircularProgress } from "@mui/material";
import React from "react";

import { formatDuration, toMs, useNow } from "./duration.js";

const numberFormat = new Intl.NumberFormat();

const StepIcon = ({ status }) => {
    const sx = { fontSize: 16, flexShrink: 0 };
    if (status === "done") {
        return <CheckCircleRoundedIcon aria-hidden sx={{ ...sx, color: "success.main" }} />;
    }
    if (status === "failed") {
        return <CancelRoundedIcon aria-hidden sx={{ ...sx, color: "error.main" }} />;
    }
    if (status === "active") {
        return (
            <Box sx={{ width: 16, display: "flex", justifyContent: "center", flexShrink: 0 }}>
                <CircularProgress aria-hidden size={12} thickness={6} />
            </Box>
        );
    }
    return <RadioButtonUncheckedRoundedIcon aria-hidden sx={{ ...sx, color: "text.disabled" }} />;
};

const STATUS_TEXT = { done: "done", active: "in progress", failed: "failed", pending: "not started" };

// Progress for tasks dominated by one opaque request (e.g. transcription):
// a list of steps, each with its status, duration and an optional item count.
// The active step's elapsed time ticks live so a long request visibly progresses.
const TaskStepsCell = ({ progress, labels = {}, countNouns = {} }) => {
    const steps = progress?.steps || [];
    const hasActive = steps.some((step) => step.status === "active");
    const now = useNow(hasActive);

    if (steps.length === 0) {
        return (
            <Box component="span" sx={{ color: "text.disabled" }}>
                —
            </Box>
        );
    }

    const firstStart = toMs(steps.find((step) => step.startedAt)?.startedAt);
    const ended = steps
        .map((step) => toMs(step.endedAt))
        .filter((time) => time !== null);
    const lastEnd = ended.length > 0 ? Math.max(...ended) : null;
    const allDone = steps.every((step) => step.status === "done");
    const failed = steps.some((step) => step.status === "failed");

    return (
        <Box sx={{ minWidth: 200, display: "flex", flexDirection: "column", gap: 0.75 }}>
            <Box
                component="ol"
                aria-label="Steps"
                sx={{
                    listStyle: "none",
                    m: 0,
                    p: 0,
                    display: "flex",
                    flexDirection: "column",
                    gap: 0.5,
                    fontSize: 13,
                }}
            >
                {steps.map((step) => {
                    const started = toMs(step.startedAt);
                    const ended = toMs(step.endedAt) ?? (step.status === "active" ? now : null);
                    const duration = started !== null && ended !== null ? ended - started : null;
                    const label = labels[step.key] || step.key;
                    const counted = Number.isInteger(step.total);
                    return (
                        <Box
                            component="li"
                            key={step.key}
                            aria-label={`${label}: ${STATUS_TEXT[step.status]}`}
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                color:
                                    step.status === "pending"
                                        ? "text.disabled"
                                        : step.status === "failed"
                                            ? "error.main"
                                            : "text.primary",
                                fontWeight: step.status === "active" ? 600 : 400,
                            }}
                        >
                            <StepIcon status={step.status} />
                            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                                {label}
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    fontSize: 12,
                                    fontWeight: 400,
                                    color: "text.secondary",
                                    fontVariantNumeric: "tabular-nums",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {counted && step.status === "active" && (
                                    <Box component="span" sx={{ mr: 1 }}>
                                        {`${numberFormat.format(step.done)} / ${numberFormat.format(step.total)}${countNouns[step.key] ? ` ${countNouns[step.key]}` : ""}`}
                                    </Box>
                                )}
                                {duration !== null && formatDuration(duration)}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {hasActive && firstStart !== null && (
                <Box sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                    {`${formatDuration(now - firstStart)} so far`}
                </Box>
            )}

            {failed && !hasActive && firstStart !== null && lastEnd !== null && (
                <Box sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                    {`Stopped after ${formatDuration(lastEnd - firstStart)}`}
                </Box>
            )}

            {allDone && (
                <Box sx={{ fontSize: 12, color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
                    {Number.isInteger(progress.dialogues) && (
                        <Box component="span" sx={{ fontWeight: 600, color: "text.primary" }}>
                            {`${numberFormat.format(progress.dialogues)} dialogues`}
                        </Box>
                    )}
                    {firstStart !== null && lastEnd !== null &&
                        `${Number.isInteger(progress.dialogues) ? " · " : ""}${formatDuration(lastEnd - firstStart)} total`}
                </Box>
            )}
        </Box>
    );
};

export default TaskStepsCell;
